import {format, startOfToday} from 'date-fns'
import {addMinutes} from 'date-fns/esm'
import React, {ChangeEvent, useEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {MenuListProps} from '@components/common/dropdown-list/dropdown-list'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {noPropagate, useBoolean} from '@containers/hooks'
import {parseTimePart, stringifyTimePart, TimePart} from '../util'
import {TwInput, TwInputContainer} from './text-input-base'

const TwList = tw.div`
  inline-flex
  w-full
  flex-col
  text-14
  leading-6
  text-main-old
  cursor-default
  select-none
  max-h-380
`

const TwListItem = tw.div`
  flex
  items-center   
  cursor-pointer
  hover:bg-action-hover
  hover:text-on-color
  pr-16
  pl-8
`

const startOfDay = startOfToday()
const options = new Array(96).fill(null).map((_x, ix) => {
  const time = addMinutes(startOfDay, ix * 15)
  return {
    label: format(time, 'HH:mm'),
    value: time
  }
})

const HoursList = <T,>(props: MenuListProps<T>) => {
  const ref = useRef<HTMLElement>()
  useEffect(() => {
    const curr = ref.current
    if (curr) {
      curr.scrollTop = 1150
    }
  }, [])
  const {items, onChange} = props
  return (
    <WithPerfectScrollbar containerRef={el => (ref.current = el)}>
      <TwList>
        {items.map((x, i) => {
          return (
            <React.Fragment key={i}>
              <TwListItem
                onMouseDown={e => {
                  e.preventDefault()
                  noPropagate(e)
                  onChange?.(x.value)
                }}
              >
                <div>{x.label}</div>
              </TwListItem>
            </React.Fragment>
          )
        })}
      </TwList>
    </WithPerfectScrollbar>
  )
}

export interface TimeTextInputProps {
  initialValue: TimePart
  onChange: (value: TimePart) => void
  onOpenChange?: (isOpen: boolean) => void
  disabled?: boolean
}

interface InputState {
  value: string
  position: number
}

type InputAction = {action: 'print'; symbol: string} | {action: 'erase'}

export const TimeTextInput = (props: TimeTextInputProps) => {
  const {
    onOpenChange,
    initialValue: initialTimePart,
    onChange: finalOnChange
  } = props
  const initialValueString = stringifyTimePart(initialTimePart)
  const initialInputState = {value: initialValueString, position: 0}
  const [inputState, setInputState] = useState(initialInputState)
  const open = useBoolean(false)
  const isDirty = useRef(false)

  useEffect(() => {
    onOpenChange?.(open.value)
  }, [open.value])

  useEffect(() => {
    const nextValueString = stringifyTimePart(initialTimePart)
    if (nextValueString !== inputState.value) {
      setInputState({value: nextValueString, position: 0})
      isDirty.current = false
    }
  }, [initialTimePart])

  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const el = inputRef.current
    if (el && document.activeElement === el) {
      const {position} = inputState
      el.selectionStart = position
      el.selectionEnd = position
    }
  }, [inputState.position])

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const el = event.target
    const position = el.selectionEnd ?? 0
    const state = getNextInputState(inputState.value, el.value, position)
    setInputState(state)
    isDirty.current = true
  }

  const onBlurHandler = () => {
    if (isDirty.current) {
      const currentValue = inputRef.current?.value ?? inputState.value
      const parsedValue = parseTimePart(currentValue)
      const validatedValueString = stringifyTimePart(parsedValue)
      if (validatedValueString !== inputState.value) {
        setInputState({
          value: validatedValueString,
          position: validatedValueString.length
        })
      }
      finalOnChange(parsedValue)
      isDirty.current = false
    }
    open.turnOff()
  }

  const onFocusHandler = () => {
    isDirty.current = false
    open.turnOn()
  }

  const setHour = (date: Date) => {
    const value = format(date, 'HH:mm')
    const parsedValue = parseTimePart(value)
    setInputState({value, position: 5})
    finalOnChange(parsedValue)
    isDirty.current = false
    open.turnOff()
  }

  return (
    <Dropdown
      hideArrow
      visible={open.value}
      onVisibleChange={() => open.setValue(false)}
      reference={inputRef}
      content={<HoursList items={options} onChange={setHour} />}
    >
      <TwInputContainer className="w-56">
        <TwInput
          className="text-center"
          ref={inputRef}
          value={inputState.value}
          onChange={onChangeHandler}
          onBlur={onBlurHandler}
          onFocus={onFocusHandler}
          disabled={props.disabled}
        />
      </TwInputContainer>
    </Dropdown>
  )
}

const getNextInputState = (
  prevValue: string,
  newValue: string,
  newPosition: number
): InputState => {
  const restored = restoreStateAndDetectAction(prevValue, newValue, newPosition)
  if (restored) {
    const [state, action] = restored
    return applyAction(state, action)
  }
  const value = stringifyTimePart(parseTimePart(newValue))
  const position = value.length
  return {value, position}
}

const restoreStateAndDetectAction = (
  prevValue: string,
  newValue: string,
  newPosition: number
): [InputState, InputAction] | undefined => {
  if (newValue.length === 1) {
    const state = {value: prevValue, position: 0}
    return [state, {action: 'print', symbol: newValue}]
  }
  const lengthDelta = newValue.length - prevValue.length
  if (lengthDelta === 1 && newPosition > 0) {
    const position = newPosition - 1
    const restoredValue = cutSymbol(newValue, position)
    if (prevValue !== restoredValue) {
      return undefined
    }
    const state = {value: prevValue, position}
    return [state, {action: 'print', symbol: newValue[position]}]
  }
  if (lengthDelta === -1) {
    const expectedValue = cutSymbol(prevValue, newPosition)
    if (expectedValue !== newValue) {
      return undefined
    }
    const state = {value: prevValue, position: newPosition + 1}
    return [state, {action: 'erase'}]
  }
  return undefined
}

type StateTransformer = (state: InputState) => InputState

const applyAction = (state: InputState, action: InputAction): InputState => {
  const steps = ([] as StateTransformer[]).concat(
    preprocess,
    action.action === 'erase'
      ? [flip, applySymbol('0'), flip]
      : applySymbol(action.symbol),
    postprocess
  )
  return steps.reduce((value, transform) => transform(value), state)
}

const applySymbol = (symbol: string): StateTransformer => {
  return state => {
    const {value, position} = state
    if (position >= value.length) {
      return state
    }
    const substitution = getSubstitution(value, position, symbol)
    if (!substitution) {
      return state
    }
    return {
      value: rewriteString(value, position, substitution),
      position: position + 1
    }
  }
}

const colonPosition = 2
const preprocess: StateTransformer = ({value, position}) => ({
  value: spliceString(value, colonPosition, 1),
  position: position + (position > colonPosition ? -1 : 0)
})
const postprocess: StateTransformer = ({value, position}) => ({
  value: spliceString(value, colonPosition, 0, ':'),
  position: position + (position >= colonPosition ? 1 : 0)
})

const getSubstitution = (
  value: string,
  position: number,
  symbol: string
): string | undefined => {
  if (!/\d/.test(symbol)) {
    return undefined
  }
  const symbolValue = +symbol
  const [hourPart1, hourPart2] = value
  switch (position) {
    case 0:
      if (symbolValue === 2 && +hourPart2 > 3) {
        return '23'
      }
      return symbolValue > 2 ? undefined : symbol
    case 1:
      return +hourPart1 === 2 && symbolValue > 3 ? undefined : symbol
    case 2:
      return symbolValue > 5 ? undefined : symbol
  }
  return symbol
}

const flip: StateTransformer = ({value, position}) => ({
  value: value.split('').reverse().join(''),
  position: value.length - position
})

const rewriteString = (str: string, position: number, value: string) =>
  spliceString(str, position, value.length, value)

const cutSymbol = (str: string, position: number) =>
  spliceString(str, position, 1)

const spliceString = (
  str: string,
  start: number,
  deleteCount: number,
  item?: string
): string => {
  const result = str.split('')
  result.splice(start, deleteCount, item ?? '')
  return result.join('')
}
